import { useEffect, useMemo, useState } from "react";
import { apiGet } from "@/lib/api";
import { fmtDateTime } from "@/lib/terminalUtils";

const FLAGS = { USD: "🇺🇸", EUR: "🇪🇺", GBP: "🇬🇧", JPY: "🇯🇵" };
const COUNTRY = { USD: "Amerika Serikat", EUR: "Zona Euro", GBP: "Inggris", JPY: "Jepang" };

const impactChip = (i) =>
  i === "high"
    ? "border-sell/50 bg-sell/10 text-sell"
    : i === "medium"
      ? "border-warn/40 bg-warn/10 text-warn"
      : "border-white/15 text-mute";

const structureColor = (v) =>
  v === "HH" || v === "LH"
    ? "text-sell"
    : v === "HL" || v === "LL"
      ? "text-buy"
      : "text-mute-dim";

const getMarkerLabel = (m) =>
  String(m?.text || m?.label || m?.title || m?.type || "").toUpperCase();

const latestStructure = (candles) => {
  const markers = Array.isArray(candles?.markers) ? candles.markers : [];
  const valid = markers
    .map((m) => ({ ...m, label: getMarkerLabel(m) }))
    .filter((m) => ["HH", "LH", "HL", "LL"].includes(m.label));
  return valid.length ? valid[valid.length - 1].label : "WAIT";
};

const EventRow = ({ ev, m5, m1, now }) => {
  const [open, setOpen] = useState(false);
  const dt = new Date(ev.time_iso);
  const diffSec = Math.floor((dt.getTime() - now.getTime()) / 1000);
  const preNews = diffSec <= 300 && diffSec > 0;
  const activeNews = diffSec <= 0 && diffSec > -300;
  const bearish = ["HH", "LH"];
  const bullish = ["HL", "LL"];
  const analysis = bearish.includes(m5) && bearish.includes(m1) ? "SELL" : bullish.includes(m5) && bullish.includes(m1) ? "BUY" : "WAIT";
  const absSec = Math.abs(diffSec);
  const countdown = diffSec > 0 ? String(Math.floor(absSec / 60)).padStart(2, "0") + ":" + String(absSec % 60).padStart(2, "0") : diffSec > -300 ? "00:00" : "DONE";

  return (
    <div
      className="overflow-hidden border border-white/10 bg-[#0d1219]"
      data-testid={`news-event-${ev.id.slice(0, 8)}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
        data-testid={`news-toggle-${ev.id.slice(0, 8)}`}
      >
        <span className="text-lg">{FLAGS[ev.currency] || "🏳️"}</span>
        <span className={`border px-1.5 py-0.5 font-mono text-[8px] font-bold ${impactChip(ev.impact)}`}>
          {String(ev.impact || "low").toUpperCase()}
        </span>
        <span className="font-mono text-[11px] font-bold text-white">{ev.currency}</span>
        <span className="min-w-0 flex-1 truncate text-xs text-mute">{ev.title}</span>
        <span className="shrink-0 font-mono text-[10px] text-mute-dim">
          {fmtDateTime(dt, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
        </span>
      </button>

      {(preNews || activeNews) && (
        <div className={`border-t px-4 py-2 font-mono ${activeNews ? "animate-pulse border-sell/40 bg-sell/5" : "border-warn/20 bg-warn/5"}`}>
          <div className="flex items-center justify-between">
            <span className="text-[8px] uppercase tracking-widest text-mute-dim">{activeNews ? "NEWS ACTIVE" : "PRE-NEWS ANALYSIS"}</span>
            <span className={`text-sm font-black ${analysis === "SELL" ? "text-sell" : analysis === "BUY" ? "text-buy" : "text-warn"}`}>{analysis}</span>
          </div>
          <div className="mt-1 flex justify-between text-[8px] text-mute-dim">
            <span>M5 {m5} · M1 {m1}</span>
            <span>{activeNews ? "Event sedang berlangsung" : `T-${countdown}`}</span>
          </div>
        </div>
      )}

      {open && (
        <div className="grid grid-cols-3 gap-px border-t border-white/10 bg-white/10 font-mono text-[10px]">
          {[["Forecast", ev.forecast], ["Previous", ev.previous], ["Actual", ev.actual]].map(([k, v]) => (
            <div key={k} className="bg-ink px-3 py-2.5 text-center">
              <div className="text-[8px] uppercase tracking-widest text-mute-dim">{k}</div>
              <div className="mt-0.5 font-bold text-white">{v || "—"}</div>
            </div>
          ))}
          <div className="col-span-3 bg-ink px-3 py-2 text-center text-[9px] text-mute-dim">
            {COUNTRY[ev.currency] || ev.currency} · {ev.source === "auto-ff" ? "ForexFactory" : "manual admin"} ·{" "}
            {fmtDateTime(dt, {
              weekday: "long",
              day: "numeric",
              month: "long",
              hour: "2-digit",
              minute: "2-digit",
              timeZoneName: "short",
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const ImpactAnalyzerCard = ({ impact, events, m5, m1, now }) => {
  const event = (events || []).map((e) => ({ ...e, dt: new Date(e.time_iso) }))
    .filter((e) => e.impact === impact && !Number.isNaN(e.dt.getTime()))
    .sort((a, b) => a.dt - b.dt)[0];
  const diffSec = event ? Math.floor((event.dt.getTime() - now.getTime()) / 1000) : null;
  const absSec = diffSec === null ? 0 : Math.abs(diffSec);
  const countdown = diffSec === null ? "—" : diffSec > 0 ? String(Math.floor(absSec / 60)).padStart(2, "0") + ":" + String(absSec % 60).padStart(2, "0") : "ACTIVE";
  const bearish = ["HH", "LH"];
  const bullish = ["HL", "LL"];
  const analysis = bearish.includes(m5) && bearish.includes(m1) ? "SELL" : bullish.includes(m5) && bullish.includes(m1) ? "BUY" : "WAIT";
  const title = impact === "high" ? "HIGH IMPACT" : impact === "medium" ? "MEDIUM IMPACT" : "LOW IMPACT";
  const box = impact === "high" ? "border-sell/50 bg-sell/5" : impact === "medium" ? "border-warn/40 bg-warn/5" : "border-white/15 bg-[#0d1219]";
  const impactText = impact === "high" ? "text-sell" : impact === "medium" ? "text-warn" : "text-mute";
  return (
    <div className={`border p-4 ${box}`}>
      <div className="flex items-center justify-between">
        <span className={`font-mono text-[10px] font-black uppercase tracking-widest ${impactText}`}>{title}</span>
        <span className="font-mono text-[9px] text-mute-dim">{countdown}</span>
      </div>
      {event ? (
        <>
          <div className="mt-3 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-xs font-bold text-white">{event.currency} · {event.title}</div>
              <div className="mt-1 font-mono text-[9px] text-mute-dim">{fmtDateTime(event.dt, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZoneName: "short" })}</div>
            </div>
            <span className={`shrink-0 font-mono text-lg font-black ${analysis === "SELL" ? "text-sell" : analysis === "BUY" ? "text-buy" : "text-warn"}`}>{analysis}</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="border border-white/10 bg-black/20 p-2"><div className="text-[7px] uppercase text-mute-dim">M5</div><div className={`font-mono text-sm font-black ${structureColor(m5)}`}>{m5}</div></div>
            <div className="border border-white/10 bg-black/20 p-2"><div className="text-[7px] uppercase text-mute-dim">M1</div><div className={`font-mono text-sm font-black ${structureColor(m1)}`}>{m1}</div></div>
          </div>
          <div className="mt-2 text-[8px] text-mute-dim">{diffSec !== null && diffSec > 0 ? "Analisis menuju news" : "News sedang / sudah berlangsung"}</div>
        </>
      ) : (
        <div className="mt-4 text-[10px] text-mute-dim">Tidak ada event {impact} terdekat.</div>
      )}
    </div>
  );
};
const StructureCard = ({ tf, value, loading }) => (
  <div className="border border-white/10 bg-[#0d1219] p-4">
    <div className="mb-3 flex items-center justify-between">
      <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-mute-dim">
        XAUUSD — {tf}
      </span>
      <span className={`rounded border px-3 py-1 font-mono text-xs font-black ${structureColor(value)} border-current`}>
        {loading ? "..." : value}
      </span>
    </div>

    <div className="grid grid-cols-2 gap-2">
      <div className="rounded border border-white/10 bg-black/20 p-3">
        <div className="text-[9px] uppercase tracking-widest text-mute-dim">Structure</div>
        <div className={`mt-1 font-mono text-lg font-black ${structureColor(value)}`}>
          {loading ? "Loading" : value}
        </div>
      </div>

      <div className="rounded border border-white/10 bg-black/20 p-3">
        <div className="text-[9px] uppercase tracking-widest text-mute-dim">Bias</div>
        <div className={`mt-1 font-mono text-sm font-bold ${
          ["HH", "LH"].includes(value)
            ? "text-sell"
            : ["HL", "LL"].includes(value)
              ? "text-buy"
              : "text-mute"
        }`}>
          {["HH", "LH"].includes(value) ? "BEARISH" : ["HL", "LL"].includes(value) ? "BULLISH" : "WAIT"}
        </div>
      </div>
    </div>
  </div>
);

export const NewsPage = () => {
  const [data, setData] = useState(null);
  const [m5, setM5] = useState("WAIT");
  const [m1, setM1] = useState("WAIT");
  const [structureLoading, setStructureLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        const [news, c5, c1] = await Promise.all([
          apiGet("/news"),
          apiGet("/market/candles?symbol=XAUUSD&interval=5m"),
          apiGet("/market/candles?symbol=XAUUSD&interval=1m"),
        ]);

        if (!alive) return;

        setData(news.data);
        setM5(latestStructure(c5.data));
        setM1(latestStructure(c1.data));
        setError(false);
      } catch {
        if (alive) setError(true);
      } finally {
        if (alive) setStructureLoading(false);
      }
    };

    load();
    const timer = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const match = (ev) => filter === "all" || ev.impact === filter;

  const upcoming = useMemo(
    () => (data?.upcoming || []).filter(match),
    [data, filter]
  );

  const past = useMemo(
    () => (data?.past || []).filter(match),
    [data, filter]
  );

  const signal = useMemo(() => {
    const bearish = ["HH", "LH"];
    const bullish = ["HL", "LL"];

    if (bearish.includes(m5) && bearish.includes(m1)) return "SELL";
    if (bullish.includes(m5) && bullish.includes(m1)) return "BUY";
    return "WAIT";
  }, [m5, m1]);

  const confirmation = useMemo(() => {
    if (signal === "WAIT") return "0/2";
    return "2/2";
  }, [signal]);

  const nextHigh = useMemo(() => {
    const list = (data?.upcoming || [])
      .filter((e) => e.impact === "high")
      .map((e) => ({ ...e, dt: new Date(e.time_iso) }))
      .filter((e) => !Number.isNaN(e.dt.getTime()) && e.dt.getTime() >= now.getTime())
      .sort((a, b) => a.dt - b.dt);

    return list[0] || null;
  }, [data, now]);

  const countdown = useMemo(() => {
    if (!nextHigh) return "—";
    const sec = Math.max(0, Math.floor((nextHigh.dt.getTime() - now.getTime()) / 1000));
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return h > 0
      ? `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
      : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [nextHigh, now]);

  const newsBias = useMemo(() => {
    if (!nextHigh) return { label: "NO HIGH IMPACT", cls: "text-mute", note: "Tidak ada event High Impact terdekat." };
    if (nextHigh.currency === "USD") {
      return signal === "BUY"
        ? { label: "CONFLICT", cls: "text-warn", note: "Technical bullish, tetapi event USD berpotensi meningkatkan volatilitas Gold." }
        : signal === "SELL"
          ? { label: "BEARISH RISK", cls: "text-sell", note: "Struktur bearish XAUUSD bertepatan dengan risiko event USD." }
          : { label: "WAIT", cls: "text-warn", note: "Event USD High Impact terdekat. Tunggu struktur M5 + M1 searah." };
    }
    return { label: "NEUTRAL", cls: "text-mute", note: `Event ${nextHigh.currency} terdekat tidak diberi bias Gold otomatis.` };
  }, [nextHigh, signal]);

  if (error && !data) {
    return (
      <div className="border border-warn/30 bg-warn/5 p-8 text-center font-mono text-xs text-warn" data-testid="news-error">
        News temporarily unavailable — scanner tetap berjalan dengan data yang tersedia
      </div>
    );
  }

  if (!data) return <p className="font-mono text-xs text-mute-dim">memuat analisa news...</p>;

  return (
    <div className="mx-auto max-w-7xl space-y-4" data-testid="news-page">

      <div className="flex flex-col gap-3 border-b border-white/10 pb-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-buy">NakedChart Analysis Center</div>
          <h1 className="mt-1 text-2xl font-black tracking-tight text-white">News & Market Analysis</h1>
          <p className="mt-1 font-mono text-[10px] text-mute-dim">
            Economic Calendar · Multi-Timeframe Structure · XAUUSD News Risk
          </p>
        </div>

        <div className="flex items-center gap-1">
          {[
            ["all", "Semua"],
            ["low", "LOW"],
            ["medium", "MED"],
            ["high", "HIGH"],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`border px-3 py-1.5 font-mono text-[9px] font-bold uppercase ${
                filter === k
                  ? "border-buy bg-buy/15 text-buy"
                  : "border-white/15 text-mute"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 xl:grid-cols-3">

        <div className="space-y-3 xl:col-span-2">
          <div className="grid gap-3 md:grid-cols-2">
            <StructureCard tf="M5" value={m5} loading={structureLoading} />
            <StructureCard tf="M1" value={m1} loading={structureLoading} />
          </div>

          <div className={`border p-5 ${
            signal === "SELL"
              ? "border-sell/50 bg-sell/5"
              : signal === "BUY"
                ? "border-buy/50 bg-buy/5"
                : "border-white/10 bg-[#0d1219]"
          }`}>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">
                  Multi-Timeframe Structure Signal
                </div>

                <div className={`mt-2 text-4xl font-black tracking-tight ${
                  signal === "SELL"
                    ? "text-sell"
                    : signal === "BUY"
                      ? "text-buy"
                      : "text-mute"
                }`}>
                  {signal === "SELL" ? "↓ SELL" : signal === "BUY" ? "↑ BUY" : "WAIT"}
                </div>

                <div className="mt-1 font-mono text-[10px] text-mute">
                  M5: <b className={structureColor(m5)}>{m5}</b>
                  {" · "}
                  M1: <b className={structureColor(m1)}>{m1}</b>
                  {" · Confirmation: "}
                  <b className="text-white">{confirmation}</b>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 md:w-64">
                <div className="border border-white/10 bg-black/20 p-3">
                  <div className="text-[8px] uppercase tracking-widest text-mute-dim">M5</div>
                  <div className={`mt-1 font-mono text-xl font-black ${structureColor(m5)}`}>{m5}</div>
                </div>
                <div className="border border-white/10 bg-black/20 p-3">
                  <div className="text-[8px] uppercase tracking-widest text-mute-dim">M1</div>
                  <div className={`mt-1 font-mono text-xl font-black ${structureColor(m1)}`}>{m1}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <ImpactAnalyzerCard impact="high" events={data?.upcoming || []} m5={m5} m1={m1} now={now} />
            <ImpactAnalyzerCard impact="medium" events={data?.upcoming || []} m5={m5} m1={m1} now={now} />
            <ImpactAnalyzerCard impact="low" events={data?.upcoming || []} m5={m5} m1={m1} now={now} />
          </div>
          <div className="border border-white/10 bg-[#0d1219]">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div>
                <div className="font-mono text-xs font-bold text-white">Economic Calendar</div>
                <div className="font-mono text-[8px] uppercase tracking-widest text-mute-dim">
                  Real ForexFactory data
                </div>
              </div>
              <div className="font-mono text-[9px] text-mute-dim">
                {upcoming.length} upcoming
              </div>
            </div>

            <div className="p-3">
              <div className="mb-2 flex gap-2 font-mono text-[9px] text-mute-dim">
                <span>TIME</span>
                <span>·</span>
                <span>LOCAL DEVICE TIMEZONE</span>
              </div>

              <div className="space-y-1.5">
                {upcoming.length === 0 && (
                  <p className="py-6 text-center font-mono text-xs text-mute-dim">
                    tidak ada event terjadwal
                  </p>
                )}
                {upcoming.map((ev) => <EventRow key={ev.id} ev={ev} m5={m5} m1={m1} now={now} />)}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">

          <div className={`border p-5 ${
            nextHigh?.impact === "high"
              ? "border-sell/40 bg-sell/5"
              : "border-white/10 bg-[#0d1219]"
          }`}>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">
              Next High Impact
            </div>

            {nextHigh ? (
              <>
                <div className="mt-3 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-black text-white">
                      {FLAGS[nextHigh.currency] || "🌐"} {nextHigh.currency}
                    </div>
                    <div className="mt-1 text-sm font-bold text-mute">{nextHigh.title}</div>
                  </div>
                  <span className="border border-sell/50 bg-sell/10 px-2 py-1 font-mono text-[8px] font-bold text-sell">
                    HIGH
                  </span>
                </div>

                <div className="mt-5 text-center">
                  <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-mute-dim">
                    Countdown
                  </div>
                  <div className="mt-1 font-mono text-3xl font-black text-white">
                    {countdown}
                  </div>
                  <div className="mt-1 font-mono text-[9px] text-mute-dim">
                    {fmtDateTime(nextHigh.dt, {
                      weekday: "short",
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                      timeZoneName: "short",
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div className="mt-5 font-mono text-xs text-mute-dim">
                Tidak ada High Impact event terdekat.
              </div>
            )}
          </div>

          <div className="border border-white/10 bg-[#0d1219] p-5">
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">
              News Impact — XAUUSD
            </div>

            <div className={`mt-3 text-xl font-black ${newsBias.cls}`}>
              {newsBias.label}
            </div>

            <p className="mt-2 text-xs leading-5 text-mute">
              {newsBias.note}
            </p>

            <div className="mt-4 border-t border-white/10 pt-4">
              <div className="flex items-center justify-between font-mono text-[9px]">
                <span className="text-mute-dim">Technical Structure</span>
                <span className={signal === "SELL" ? "text-sell" : signal === "BUY" ? "text-buy" : "text-mute"}>
                  {signal}
                </span>
              </div>

              <div className="mt-2 flex items-center justify-between font-mono text-[9px]">
                <span className="text-mute-dim">News Risk</span>
                <span className={nextHigh ? "text-sell" : "text-buy"}>
                  {nextHigh ? "HIGH" : "LOW"}
                </span>
              </div>
            </div>
          </div>

          <div className="border border-white/10 bg-[#0d1219] p-5">
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">
              Analysis Rule
            </div>

            <div className="mt-3 space-y-2 font-mono text-[9px]">
              <div className="flex justify-between border-b border-white/10 pb-2">
                <span className="text-mute">M5 + M1 HH/LH</span>
                <span className="font-bold text-sell">SELL</span>
              </div>
              <div className="flex justify-between border-b border-white/10 pb-2">
                <span className="text-mute">M5 + M1 HL/LL</span>
                <span className="font-bold text-buy">BUY</span>
              </div>
              <div className="flex justify-between">
                <span className="text-mute">Structure mixed</span>
                <span className="font-bold text-warn">WAIT</span>
              </div>
            </div>

            <div className="mt-4 border border-buy/20 bg-buy/5 p-3 font-mono text-[8px] leading-4 text-mute-dim">
              Analyzer ini berdiri terpisah dari Trading Engine utama.
              Tidak mengubah signal engine atau strategy.
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="mb-2 font-mono text-[10px] font-bold text-mute-dim">
          ▼ SUDAH LEWAT ({past.length})
        </div>

        <div className="space-y-1.5 opacity-60">
          {past.map((ev) => <EventRow key={ev.id} ev={ev} m5={m5} m1={m1} now={now} />)}
        </div>
      </div>
    </div>
  );
};
